import { logger } from './logger';

export interface ParsedFormResponse {
  Form?: {
    Type: string;
    Title?: string;
  };
  Reply_Message: string;
  [key: string]: any;
}

export class ChatFormParser {
  static parseFormResponse(aiResponse: string): ParsedFormResponse | null {
    logger.log('🔍 ChatFormParser: Processing AI response for forms');
    
    // Strategy 1: Try to parse complete JSON
    const jsonResponse = this.tryParseCompleteJSON(aiResponse);
    if (jsonResponse) return jsonResponse;

    // Strategy 2: Extract Form object pattern
    const formObjectResponse = this.tryExtractFormObject(aiResponse);
    if (formObjectResponse) return formObjectResponse;

    // Strategy 3: Look for Form.Type patterns in plain text
    const plainTextResponse = this.tryExtractPlainTextForm(aiResponse);
    if (plainTextResponse) return plainTextResponse;

    // Strategy 4: Check for vehicle-related content
    const vehicleResponse = this.tryDetectVehicleContent(aiResponse);
    if (vehicleResponse) return vehicleResponse;

    return null;
  }

  private static tryParseCompleteJSON(aiResponse: string): ParsedFormResponse | null {
    try {
      const jsonMatch = aiResponse.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed.Form?.Type) {
          logger.log('🔍 ChatFormParser: Successfully parsed complete JSON');
          return parsed;
        }
      }
    } catch (error) {
      logger.log('🔍 ChatFormParser: Complete JSON parsing failed:', error);
    }
    return null;
  }

  private static tryExtractFormObject(aiResponse: string): ParsedFormResponse | null {
    try {
      const formObjectMatch = aiResponse.match(/"Form":\s*\{[^}]*\}/);
      if (formObjectMatch) {
        logger.log('🔍 ChatFormParser: Found Form object pattern');
        
        const typeMatch = formObjectMatch[0].match(/"Type":\s*"([^"]+)"/);
        const titleMatch = formObjectMatch[0].match(/"Title":\s*"([^"]+)"/);
        
        if (typeMatch) {
        return {
          Form: {
            Type: typeMatch[1],
            Title: titleMatch?.[1]
          },
          Reply_Message: aiResponse
        };
        }
      }
    } catch (error) {
      logger.log('🔍 ChatFormParser: Form object extraction failed:', error);
    }
    return null;
  }

  private static tryExtractPlainTextForm(aiResponse: string): ParsedFormResponse | null {
    const formTypeMatch = aiResponse.match(/["']?Form\.?Type["']?\s*:\s*["']?(\w+)["']?/i);
    const formTitleMatch = aiResponse.match(/["']?Form\.?Title["']?\s*:\s*["']?([^"',\n]+)["']?/i);
    
    if (formTypeMatch) {
      logger.log('🔍 ChatFormParser: Found Form.Type in plain text');
      return {
        Form: {
          Type: formTypeMatch[1],
          Title: formTitleMatch?.[1]
        },
        Reply_Message: aiResponse
      };
    }
    return null;
  }

  private static tryDetectVehicleContent(aiResponse: string): ParsedFormResponse | null {
    const hasVehicleListingPattern = /\b(?:here\s+(?:are|is)|found|available|inventory|vehicles?|cars?|models?)\b.*\b(?:you\s+can|available|for\s+sale|in\s+stock|interested)\b/i;
    const hasVehicleKeywords = /\b(?:inventory|vehicles?|cars?|models?|available|certified|pre-owned|used|new|SUVs?)\b/i.test(aiResponse);
    
    if (hasVehicleListingPattern.test(aiResponse) || hasVehicleKeywords) {
      logger.log('🔍 ChatFormParser: Vehicle-related content detected');
      return {
        Form: {
          Type: 'Contact',
          Title: 'Contact Us About These Vehicles'
        },
        Reply_Message: aiResponse
      };
    }
    return null;
  }

  static hasFormPattern(aiResponse: string): boolean {
    return (
      aiResponse.includes('"Form"') && 
      aiResponse.includes('"Type"') && 
      aiResponse.includes('"Contact"')
    );
  }
}